import { getLanguage } from "@excalidraw/excalidraw/i18n";

import { atom, appJotaiStore } from "../app-jotai";

import fallbackLangData from "./data/en.json";

// Locale files live in `./data/` and load through an explicit glob map, not a
// template-literal `import()`. Vite's dynamic-import-vars plugin refuses a
// variable that would glob the importing module's own directory, so
// `import(\`./${code}.json\`)` silently shipped no bundle data at all. The glob
// is statically analysable, so it cannot produce nothing; exported for tests.
export const localeLoaders = import.meta.glob<{ default: Record<string, any> }>(
  "./data/*.json",
);

/**
 * Bumped after a language file finishes loading, so hooks can re-render at
 * the right moment. The app's own locale files load asynchronously, separate
 * from upstream's, so this atom is the signal that ours are ready.
 */
export const drawolaLocaleVersionAtom = atom(0);

/**
 * Code of the language data that is applied now: English at start, after a
 * failed load, or the code of the last load that won. Unlike the requested
 * app language, it never names a language that is still loading.
 */
export const drawolaLocaleCodeAtom = atom("en");

let currentLangData: Record<string, any> = fallbackLangData;

// Monotonic id for in-flight loads. A load that resolves after a newer one
// started is stale and must not apply its data or bump the version atom.
let loadRequestId = 0;

/**
 * Load the app's own locale file for `code` into the module-level data.
 * `"en"` (and anything that fails to load) uses the statically imported
 * fallback. Bumps the version atom when this call wins, so subscribers
 * re-render. A stale result (a newer call started while this one was in
 * flight) is dropped: it neither applies its data nor bumps the atom.
 */
export const loadDrawolaLocale = async (code: string): Promise<void> => {
  const requestId = ++loadRequestId;
  let appliedCode = code;
  if (code === "en") {
    currentLangData = fallbackLangData;
  } else {
    try {
      const loader = localeLoaders[`./data/${code}.json`];
      if (!loader) {
        throw new Error(`no locale file for ${code}`);
      }
      const data = await loader();
      if (requestId !== loadRequestId) {
        return;
      }
      currentLangData = data.default;
    } catch (error: any) {
      if (requestId !== loadRequestId) {
        return;
      }
      console.error(`Failed to load drawola locale ${code}:`, error.message);
      currentLangData = fallbackLangData;
      appliedCode = "en";
    }
  }
  appJotaiStore.set(drawolaLocaleCodeAtom, appliedCode);
  appJotaiStore.set(drawolaLocaleVersionAtom, (v) => v + 1);
};

const findPartsForData = (data: any, parts: string[]) => {
  for (let index = 0; index < parts.length; ++index) {
    const part = parts[index];
    if (data[part] === undefined) {
      return undefined;
    }
    data = data[part];
  }
  if (typeof data !== "string") {
    return undefined;
  }
  return data;
};

/**
 * Translate a fork string from the app's own locale files. Mirrors upstream's
 * `t()`: resolves the dot-separated path against the current language data,
 * then against the English fallback. A missing key in every file is an error
 * in development and a warning plus empty string in production.
 */
export const translateDrawola = (
  key: string,
  replacement?: { [key: string]: string | number },
): string => {
  if (getLanguage().code.startsWith("__test__")) {
    const name = replacement
      ? `${key}(${JSON.stringify(replacement).slice(1, -1)})`
      : key;
    return `\u{202a}[[${name}]]\u{202c}`;
  }

  const parts = key.split(".");
  let translation =
    findPartsForData(currentLangData, parts) ||
    findPartsForData(fallbackLangData, parts);
  if (translation === undefined) {
    const errorMessage = `Can't find translation for ${key}`;
    // in production, don't blow up the app on a missing translation key
    if (import.meta.env.PROD) {
      console.warn(errorMessage);
      return "";
    }
    throw new Error(errorMessage);
  }

  if (replacement) {
    for (const k in replacement) {
      translation = translation.replace(`{{${k}}}`, String(replacement[k]));
    }
  }
  return translation;
};
